/**
 * "Workload" panel — top assignees by OPEN-task count, with an
 * overdue chip on the right when the user is carrying at least one
 * overdue item. The API already returns the rows pre-joined with
 * avatar + name; this file is layout only.
 *
 * Falls back to a tasteful empty state when no one has open tasks
 * in the Space (e.g. a fresh Space with only completed work or
 * tasks assigned only to unassigned).
 */

import { Avatar } from '../Avatar.jsx'

export function SpaceOverviewWorkload({ overview }) {
  const rows = overview?.workload?.topAssignees ?? []
  // These rows are scoped to whatever the overview is scoped to, so the empty
  // state must name the same thing — "in this Space" while showing one List's
  // workload is the mismatch that made these numbers look wrong.
  const scopeLabel = overview?.scope?.listName
    ? `in ${overview.scope.listName}`
    : 'in this Space'
  return (
    <div className="rounded-lg border border-[color:var(--color-rule)] bg-[color:var(--color-card)] p-4">
      <div className="flex items-baseline justify-between mb-3">
        <p className="font-display text-lg font-medium tracking-tight text-[color:var(--color-ink-strong)]">
          Workload
        </p>
        <p className="text-xs text-[color:var(--color-ink-muted)]">
          Top assignees by open tasks
        </p>
      </div>

      {rows.length === 0 ? (
        <p className="text-xs text-[color:var(--color-ink-muted)]">
          No open tasks assigned {scopeLabel} yet.
        </p>
      ) : (
        <ul className="space-y-2">
          {rows.map((r) => (
            <li
              key={r.userId}
              className="flex items-center gap-3 px-2 py-1.5 rounded-md hover:bg-[color:var(--color-card-hover)]"
            >
              <Avatar
                name={r.fullName}
                avatarUrl={r.avatarUrl}
                size="sm"
                colorByName
              />
              <span className="flex-1 truncate text-sm text-[color:var(--color-ink)]">
                {r.fullName}
              </span>
              {r.overdueCount > 0 ? (
                <span
                  className="text-[11px] font-semibold tabular-nums px-2 py-0.5 rounded-full"
                  style={{
                    color: 'var(--color-priority-urgent)',
                    backgroundColor: 'color-mix(in srgb, var(--color-priority-urgent) 12%, transparent)',
                  }}
                  title={`${r.overdueCount} overdue`}
                >
                  {r.overdueCount} overdue
                </span>
              ) : null}
              <span className="text-sm tabular-nums text-[color:var(--color-ink-muted)] min-w-[2ch] text-right">
                {r.openCount}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
